/**
 * IANA timezone helpers for topic authoring and display.
 */

import { formatInTimeZone } from 'date-fns-tz';

export interface TimezoneOption {
  label: string;
  value: string;
}

/**
 * US time zones offered in the topic picker, ordered by descending standard
 * (non-DST) GMT offset so the order stays stable year-round.
 * Labels are the friendly US names; values are canonical IANA zone ids.
 */
export const US_TIMEZONES: readonly TimezoneOption[] = [
  { label: 'Chamorro (Guam, MP)', value: 'Pacific/Guam' },
  { label: 'Atlantic (PR, USVI)', value: 'America/Puerto_Rico' },
  { label: 'Eastern', value: 'America/New_York' },
  { label: 'Central', value: 'America/Chicago' },
  { label: 'Mountain', value: 'America/Denver' },
  { label: 'Arizona (no DST)', value: 'America/Phoenix' },
  { label: 'Pacific', value: 'America/Los_Angeles' },
  { label: 'Alaska', value: 'America/Anchorage' },
  { label: 'Hawaii', value: 'Pacific/Honolulu' },
  { label: 'Samoa', value: 'Pacific/Pago_Pago' },
];

export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function normalizeTimezone(tz: string | null | undefined): string {
  return isValidTimezone(tz) ? tz : 'UTC';
}

export function getBrowserTimezone(): string {
  return normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * Returns the timezone options for the topic picker.
 * Starts with US_TIMEZONES, then appends up to two extras using raw IANA
 * names as both label and value:
 *   1. The browser's timezone, if it isn't already in the list.
 *   2. The topic's stored timezone, if it isn't already in the list and
 *      doesn't match the browser timezone.
 */
export function getAvailableTimezones(
  browserTz: string,
  storedTopicTz?: string | null
): readonly TimezoneOption[] {
  const knownValues = new Set(US_TIMEZONES.map(z => z.value));
  const extras: TimezoneOption[] = [];

  const safeBrowserTz = normalizeTimezone(browserTz);
  if (!knownValues.has(safeBrowserTz)) {
    extras.push({ label: safeBrowserTz, value: safeBrowserTz });
    knownValues.add(safeBrowserTz);
  }

  if (storedTopicTz) {
    const safeTopicTz = normalizeTimezone(storedTopicTz);
    if (!knownValues.has(safeTopicTz)) {
      extras.push({ label: safeTopicTz, value: safeTopicTz });
    }
  }

  return extras.length ? [...US_TIMEZONES, ...extras] : US_TIMEZONES;
}

/**
 * Convert a local wall-clock time ("YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss")
 * interpreted in the given IANA zone to a UTC Date.
 *
 * Round-trip trick: treat the wall clock as if it were UTC to get a probe
 * instant; format that probe into `tz` to see what wall-clock it lands on;
 * the difference is the zone's offset at that moment, which we add back.
 */
export function zonedWallClockToUTC(wallClock: string, tz: string): Date {
  const normalized =
    wallClock.length === 16 ? `${wallClock}:00` : wallClock.slice(0, 19);
  const probe = new Date(`${normalized}Z`);
  if (isNaN(probe.getTime())) {
    throw new Error(`Invalid wallClock: ${wallClock}`);
  }
  const safeTimezone = normalizeTimezone(tz);
  const zonedString = probe.toLocaleString('sv-SE', {
    timeZone: safeTimezone,
    hour12: false,
  });
  const zoned = new Date(`${zonedString.replace(' ', 'T')}Z`);
  if (isNaN(zoned.getTime())) {
    return probe;
  }
  const delta = probe.getTime() - zoned.getTime();
  return new Date(probe.getTime() + delta);
}

/**
 * Returns a short label like "America/Los_Angeles (PDT)" for display.
 * Falls back to just the IANA name if the browser can't produce an abbrev.
 */
export function formatTimezoneLabel(tz: string, at: Date = new Date()): string {
  try {
    const safeTimezone = normalizeTimezone(tz);
    const abbrev = formatInTimeZone(at, safeTimezone, 'zzz');
    return abbrev ? `${safeTimezone} (${abbrev})` : safeTimezone;
  } catch {
    return normalizeTimezone(tz);
  }
}
